<template>
  <div class="overflow-x-auto mt-8">
    <h2 class="text-xl font-semibold mb-4 text-gray-700">Uploaded Pages</h2>
    <table class="min-w-full bg-white border border-gray-300 rounded-lg shadow">
      <thead>
      <tr>
        <th class="py-3 px-6 text-left text-sm font-medium text-gray-700 bg-gray-100 border-b">Page Preview</th>
        <th class="py-3 px-6 text-left text-sm font-medium text-gray-700 bg-gray-100 border-b">File Type</th>
        <th class="py-3 px-6 text-left text-sm font-medium text-gray-700 bg-gray-100 border-b">Actions</th>
      </tr>
      </thead>
      <tbody>
      <tr v-if="pages.length === 0">
        <td colspan="3" class="text-center py-4 text-gray-500">No pages have been uploaded yet.</td>
      </tr>
      <tr v-for="page in pages" :key="page.id" class="border-b hover:bg-gray-50">
        <td class="py-3 px-6">
          <div class="block w-20 h-20 rounded-md border flex items-center justify-center bg-gray-50">
            <!-- --- TEMPLATE FIX --- -->
            <!-- This block now handles ALL file types uniformly. -->
            <!-- It will show the image (PDF icon or thumbnail) once its object URL is loaded. -->
            <img
                v-if="thumbnailUrls[page.id]"
                :src="thumbnailUrls[page.id]"
                alt="Page Preview"
                class="w-full h-full object-contain"
            />
            <!-- Show a spinner while the image is loading via Axios -->
            <div v-else class="w-full h-full flex items-center justify-center">
              <svg class="animate-spin h-5 w-5 text-gray-400" fill="none" viewBox="0 0 24 24">
                <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"></circle>
                <path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V4a10 10 0 00-10 10h2z"></path>
              </svg>
            </div>
          </div>
        </td>
        <td class="py-3 px-6 text-sm text-gray-700 align-middle">
          <span class="px-2 py-1 text-xs rounded-full bg-gray-100 text-gray-800 font-mono">{{ page.mimeType }}</span>
        </td>
        <td class="py-3 px-6 text-sm align-middle">
          <button @click="deletePage(page.id)" class="text-red-500 hover:text-red-700">Delete</button>
        </td>
      </tr>
      </tbody>
    </table>
  </div>
</template>

<script setup>
import { ref, onMounted, onBeforeUnmount } from 'vue'
import { useRoute } from 'vue-router'
import SockJS from 'sockjs-client'
import Stomp from 'stompjs'
import axios from '../services/axiosInstance'
import { useModalStore } from '@/stores/modal'

const route = useRoute()
const pages = ref([])
const stompClient = ref(null)
const thumbnailUrls = ref({})
const modal = useModalStore()

const fetchThumbnails = async () => {
  const thumbnailPromises = pages.value.map(async (page) => {
    // --- SCRIPT FIX ---
    // The special check for PDFs has been removed.
    // Axios will now be called for ALL file types.
    try {
      const testId = route.params.testId
      const url = `/tests/${testId}/questions/pages/${page.id}/thumbnail`
      const response = await axios.get(url, { responseType: 'blob' })
      const objectURL = URL.createObjectURL(response.data)
      thumbnailUrls.value[page.id] = objectURL
    } catch (error) {
      console.error(`Error fetching thumbnail for page ${page.id}:`, error)
    }
  })
  await Promise.all(thumbnailPromises)
}

const fetchPages = async () => {
  try {
    const testId = route.params.testId
    if (!testId) return
    const response = await axios.get(`/tests/${testId}/questions/pages`)
    pages.value = response.data
    // After fetching the page list, fetch all their thumbnails
    await fetchThumbnails()
  } catch (error) {
    console.error('Error fetching pages:', error)
  }
}

const deletePage = async (pageId) => {
  const confirmed = await modal.confirm('Are you sure you want to delete this page?', 'Delete Page', 'Delete')
  if (!confirmed) return

  try {
    const testId = route.params.testId
    await axios.delete(`/tests/${testId}/questions/pages/${pageId}`) // (or answers/pages)
    await fetchPages()
  } catch (error) {
    console.error('Error deleting page:', error)
    modal.error('Failed to delete page.')
  }
}

// WebSocket for real-time updates
const setupWebSocket = () => {
  const socket = new SockJS(process.env.VUE_APP_WS_URL)
  stompClient.value = Stomp.over(socket)
  stompClient.value.debug = null

  stompClient.value.connect({}, () => {
    const testId = route.params.testId
    if (!testId) return
    stompClient.value.subscribe(`/topic/tests/${testId}/questions/pages`, (message) => {
      const body = JSON.parse(message.body)
      if (body.type === 'page_uploaded' || body.type === 'page_deleted') {
        fetchPages()
      }
    })
  }, error => console.error('WebSocket connection error:', error))
}

onMounted(() => {
  fetchPages()
  setupWebSocket()
})

onBeforeUnmount(() => {
  Object.values(thumbnailUrls.value).forEach(url => URL.revokeObjectURL(url))
  if (stompClient.value && stompClient.value.connected) {
    stompClient.value.disconnect(() => console.log('Disconnected'))
  }
})

// Expose pages for parent component access
defineExpose({
  pages
})
</script>
